import { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import { B3Card } from '@/components/B3Card';
import { B3CustomForm } from '@/components/B3CustomForm';
import CustomButton from '@/components/button/CustomButton';
import { Captcha } from '@/components/captcha/Captcha';
import B3Spin from '@/components/spin/B3Spin';
import { useMobile } from '@/hooks/useMobile';
import { useB3Lang } from '@/lib/lang';
import { CustomStyleContext } from '@/shared/customStyleButton';
import { GlobalContext } from '@/shared/global';
import { getStorefrontToken, requestResetPassword } from '@/shared/service/b2b/graphql/recaptcha';
import b2bLogger from '@/utils/b3Logger';

import { getForgotPasswordFields } from '../Login/helper';
import LoginImage from '../Login/LoginImage';
import { B3ResetPassWordButton } from '../Login/styled';
import { type PageProps } from '../PageProps';

import { sendForgotPasswordEmailFor } from './sendForgotPasswordEmailFor';

interface ForgotPasswordProps extends PageProps {
  logo?: string;
  isEnabledOnStorefront: boolean;
  storefrontSiteKey: string;
}

interface FormFields {
  email: string;
}

export function ForgotPassword({
  setOpenPage,
  isEnabledOnStorefront,
  storefrontSiteKey,
  logo,
}: ForgotPasswordProps) {
  const [isMobile] = useMobile();
  const [isLoading, setLoading] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const b3Lang = useB3Lang();
  const forgotPasswordFields = getForgotPasswordFields(b3Lang);
  const [isCaptchaMissing, setIsCaptchaMissing] = useState(false);
  const [captchaKey, setCaptchaKey] = useState('');

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    setValue,
  } = useForm<FormFields>({
    mode: 'onSubmit',
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (captchaKey || !isEnabledOnStorefront) setIsCaptchaMissing(false);
  }, [captchaKey, isEnabledOnStorefront]);

  const handleLoginClick = handleSubmit(async ({ email }) => {
    if (isEnabledOnStorefront && !captchaKey) {
      setIsCaptchaMissing(true);
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      if (isEnabledOnStorefront && captchaKey) {
        try {
          await requestResetPassword(captchaKey, email);
          navigate('/login?loginFlag=receivePassword');
        } catch (e) {
          b2bLogger.error(e);
        }
      }

      if (!isEnabledOnStorefront) {
        await sendForgotPasswordEmailFor(email);
        navigate('/login?loginFlag=receivePassword');
      }
    } catch (e) {
      b2bLogger.error(e);
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  });

  return (
    <B3Card setOpenPage={setOpenPage}>
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          mr: 'auto',
          ml: 'auto',
          maxWidth: '537px',
        }}
      >
        <Box sx={{ mt: '20px' }}>
          {logo && (
            <LoginImage
              alt={b3Lang('global.tips.registerLogo')}
              src={logo}
              maxWidth={isMobile ? '175px' : '250px'}
              onClick={() => {
                window.location.assign('/');
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            margin: '20px 0 0 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              margin: '16px 0',
            }}
          >
            {b3Lang('forgotPassword.resetPassword')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              margin: '0 16px 16px 16px',
            }}
          >
            {b3Lang('forgotPassword.requestEmail')}
          </Typography>
          <B3CustomForm
            formFields={forgotPasswordFields}
            errors={errors}
            control={control}
            getValues={getValues}
            setValue={setValue}
            sx={{ margin: '0 16px', maxWidth: isMobile ? '311px' : '505px' }}
          />
          {isEnabledOnStorefront && isCaptchaMissing ? (
            <Typography
              variant="body1"
              sx={{
                color: 'red',
                display: 'flex',
                alignSelf: 'flex-start',
                marginLeft: '18px',
                marginTop: '2px',
                fontSize: '13px',
              }}
            >
              {b3Lang('login.loginText.missingCaptcha')}
            </Typography>
          ) : (
            ''
          )}
          {isEnabledOnStorefront ? (
            <Box sx={{ marginTop: '20px' }}>
              <Captcha siteKey={storefrontSiteKey} size="normal" handleGetKey={setCaptchaKey} />
            </Box>
          ) : (
            ''
          )}
          <B3Spin isSpinning={isLoading} size={20} isFlex={false}>
            <B3ResetPassWordButton>
              <CustomButton
                type="submit"
                size="medium"
                onClick={handleLoginClick}
                variant="contained"
                disabled={isLoading}
                sx={{ width: 'auto' }}
              >
                {b3Lang('forgotPassword.resetPasswordBtn')}
              </CustomButton>
            </B3ResetPassWordButton>
          </B3Spin>
        </Box>
      </Box>
    </B3Card>
  );
}

export default function Page({ setOpenPage }: PageProps) {
  const { logo } = useContext(GlobalContext).state;
  const { loginPageDisplay } = useContext(CustomStyleContext).state;
  const [isEnabledOnStorefront, setIsEnabledOnStorefront] = useState(false);
  const [storefrontSiteKey, setStorefrontSiteKey] = useState('');

  useEffect(() => {
    const getIsEnabledOnStorefront = async () => {
      try {
        const response = await getStorefrontToken();

        if (response) {
          setIsEnabledOnStorefront(response.isEnabledOnStorefront);
          setStorefrontSiteKey(response.siteKey);
        }
      } catch (e) {
        b2bLogger.error(e);
      }
    };

    getIsEnabledOnStorefront();
  }, []);

  return (
    <ForgotPassword
      setOpenPage={setOpenPage}
      logo={loginPageDisplay.displayStoreLogo ? logo : undefined}
      isEnabledOnStorefront={isEnabledOnStorefront}
      storefrontSiteKey={storefrontSiteKey}
    />
  );
}
